/**
 * <product-carousel> custom element.
 *
 * Runtime contract:
 * - Liquid provides [data-pc-viewport], [data-pc-slide], prev/next buttons,
 *   status text, and Embla UMD asset URLs.
 * - This file loads Embla and optional Embla Class Names lazily.
 * - Embla v9 API/event names are used:
 *   goToPrev/goToNext/goTo, canGoToPrev/canGoToNext, selectedSnap,
 *   previousSnap, snapList, slideNodes, slidesInView, reinit,
 *   pointerdown, slidesinview.
 */
(() => {
  if (customElements.get('product-carousel')) return;

  const scriptPromises = new Map();

  const existingScript = (src) => {
    if (!src) return null;
    return Array.from(document.scripts).find((script) => script.src === src) || null;
  };

  const loadGlobal = (src, globalName) => {
    if (window[globalName]) return Promise.resolve(window[globalName]);
    if (!src) return Promise.resolve(null);
    const key = `${globalName}:${src}`;
    if (scriptPromises.has(key)) return scriptPromises.get(key);

    const promise = new Promise((resolve) => {
      const done = () => resolve(window[globalName] || null);
      const fail = () => resolve(null);
      const current = existingScript(src);
      if (current) {
        current.addEventListener('load', done, { once: true });
        current.addEventListener('error', fail, { once: true });
        setTimeout(done, 5000);
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.productCarouselDependency = globalName;
      script.addEventListener('load', done, { once: true });
      script.addEventListener('error', fail, { once: true });
      document.head.appendChild(script);
    });

    scriptPromises.set(key, promise);
    return promise;
  };

  const safeTemplate = (template, values) => {
    return Object.entries(values).reduce((text, [key, value]) => {
      return text.replaceAll(`[[${key}]]`, String(value));
    }, template);
  };

  class ProductCarousel extends HTMLElement {
    #abort = null;
    #embla = null;
    #viewport = null;
    #prevButton = null;
    #nextButton = null;
    #status = null;
    #usingClassNames = false;
    #userInteracted = false;

    connectedCallback() {
      if (this.#abort) return;
      this.#abort = new AbortController();
      this.#viewport = this.querySelector('[data-pc-viewport]');
      this.#prevButton = this.querySelector('[data-pc-prev]');
      this.#nextButton = this.querySelector('[data-pc-next]');
      this.#status = this.querySelector('[data-pc-status]');

      this.#prevButton?.addEventListener('click', this.#goPrev, {
        signal: this.#abort.signal,
      });
      this.#nextButton?.addEventListener('click', this.#goNext, {
        signal: this.#abort.signal,
      });
      this.addEventListener('keydown', this.#onKeydown, {
        signal: this.#abort.signal,
      });

      this.#initWhenReady();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
      this.#destroyEmbla();
      this.removeAttribute('data-pc-ready');
      this.removeAttribute('data-pc-scrollable');
      this.#clearStateClasses();
      this.#viewport = null;
      this.#prevButton = null;
      this.#nextButton = null;
      this.#status = null;
      this.#usingClassNames = false;
      this.#userInteracted = false;
    }

    async #initWhenReady() {
      const abort = this.#abort;
      const Embla = await loadGlobal(this.dataset.emblaSrc, 'EmblaCarousel');
      const ClassNames = await loadGlobal(
        this.dataset.emblaClassNamesSrc,
        'EmblaCarouselClassNames'
      );
      if (!Embla || !abort || abort.signal.aborted || !this.isConnected) return;
      this.#initEmbla(Embla, ClassNames);
    }

    #initEmbla(Embla, ClassNames) {
      if (this.#embla || !this.#viewport) return;
      const loop = this.dataset.loop !== 'false';
      const usesPeek = this.#usesPeek();
      const align = usesPeek ? 'center' : 'start';
      const breakpoints = {
        '(prefers-reduced-motion: reduce)': { duration: 0 },
      };
      if (!usesPeek) {
        breakpoints['(max-width: 768px)'] = { align: 'start' };
      }

      const plugins = [];
      if (typeof ClassNames === 'function') {
        this.#usingClassNames = true;
        plugins.push(
          ClassNames({
            snapped: 'is-pc-snapped',
            inView: 'is-pc-in-view',
            draggable: 'is-pc-draggable',
            dragging: 'is-pc-pointerdown',
            loop: 'is-pc-loop',
          })
        );
      }

      this.#embla = Embla(
        this.#viewport,
        {
          loop,
          align,
          slidesToScroll: this.#slidesToScroll(),
          dragFree: false,
          inViewThreshold: 1,
          breakpoints,
        },
        plugins
      );

      this.#embla
        .on('select', this.#onSelect)
        .on('reinit', this.#onSelect)
        .on('slidesinview', this.#onSlidesInView)
        .on('reinit', this.#onSlidesInView)
        .on('pointerdown', this.#onPointerDown);

      this.dataset.pcReady = 'true';
      this.#sync();
      this.#syncInViewClasses();
      this.#dispatch('init');
    }

    #destroyEmbla() {
      if (!this.#embla) return;
      this.#embla
        .off('select', this.#onSelect)
        .off('reinit', this.#onSelect)
        .off('slidesinview', this.#onSlidesInView)
        .off('reinit', this.#onSlidesInView)
        .off('pointerdown', this.#onPointerDown);
      this.#dispatch('destroy');
      this.#embla.destroy();
      this.#embla = null;
    }

    #usesPeek() {
      return this.dataset.peek !== 'false';
    }

    #slidesToScroll() {
      if (this.dataset.slidesToScroll === 'auto') return 'auto';
      const parsed = Number(this.dataset.slidesToScroll || 1);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    #goPrev = () => {
      this.#userInteracted = true;
      this.#embla?.goToPrev();
    };

    #goNext = () => {
      this.#userInteracted = true;
      this.#embla?.goToNext();
    };

    #onKeydown = (event) => {
      if (!this.#embla || !this.contains(document.activeElement)) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#userInteracted = true;
        this.#embla.goToPrev();
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#userInteracted = true;
        this.#embla.goToNext();
      }
    };

    #onPointerDown = () => {
      this.#userInteracted = true;
    };

    #onSelect = () => {
      this.#sync();
      this.#dispatch('select');
    };

    #onSlidesInView = () => {
      this.#syncInViewClasses();
      this.#syncStatus();
    };

    #sync() {
      if (!this.#embla) return;
      const canGoToPrev = this.#embla.canGoToPrev();
      const canGoToNext = this.#embla.canGoToNext();
      if (this.#prevButton) this.#prevButton.disabled = !canGoToPrev;
      if (this.#nextButton) this.#nextButton.disabled = !canGoToNext;
      this.toggleAttribute('data-pc-scrollable', canGoToPrev || canGoToNext);
      this.#syncStatus();
    }

    #syncInViewClasses() {
      if (!this.#embla || this.#usingClassNames) return;
      const inView = new Set(this.#embla.slidesInView());
      this.#embla.slideNodes().forEach((slide, index) => {
        slide.classList.toggle('is-pc-in-view', inView.has(index));
        slide.classList.toggle('is-pc-snapped', index === this.#embla.selectedSnap());
      });
    }

    #clearStateClasses() {
      this.querySelectorAll('[data-pc-slide]').forEach((slide) => {
        slide.classList.remove(
          'is-pc-in-view',
          'is-pc-snapped',
          'is-pc-draggable',
          'is-pc-pointerdown',
          'is-pc-loop'
        );
      });
    }

    #syncStatus() {
      if (!this.#status || !this.#embla || !this.#userInteracted) return;
      const total = this.#embla.slideNodes().length;
      const inView = this.#embla.slidesInView();
      if (!total || !inView.length) return;
      const first = Math.min(...inView) + 1;
      const last = Math.max(...inView) + 1;
      const singleTemplate =
        this.#status.dataset.pcStatusSingleTemplate ||
        'Showing product [[first]] of [[total]]';
      const rangeTemplate =
        this.#status.dataset.pcStatusRangeTemplate ||
        'Showing products [[first]]–[[last]] of [[total]]';
      const template = first === last ? singleTemplate : rangeTemplate;
      this.#status.textContent = '';
      requestAnimationFrame(() => {
        if (!this.#status) return;
        this.#status.textContent = safeTemplate(template, {
          first,
          last,
          total,
        });
      });
    }

    #dispatch(name) {
      this.dispatchEvent(
        new CustomEvent(`product-carousel:${name}`, {
          bubbles: true,
          detail: {
            selectedSnap: this.#embla?.selectedSnap?.() ?? 0,
            previousSnap: this.#embla?.previousSnap?.() ?? 0,
            snapCount: this.#embla?.snapList?.().length ?? 0,
            slideCount: this.#embla?.slideNodes?.().length ?? 0,
            slidesInView: this.#embla?.slidesInView?.() ?? [],
          },
        })
      );
    }
  }

  if (!customElements.get('product-carousel')) {
    customElements.define('product-carousel', ProductCarousel);
  }
})();
